import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor, type PluginListenerHandle } from '@capacitor/core'

export type AppLifecycleEvent = 'pause' | 'resume' | 'backgrounded' | 'foregrounded'

export interface AppLifecycleSubscription {
  remove: () => void
}

/** True if running inside a Capacitor native shell. */
export const isNativePlatform = (): boolean => {
  try {
    return Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

/**
 * Subscribes to app lifecycle events. On Capacitor native platforms, wires
 * @capacitor/app's appStateChange + pause + resume. On web, subscribes to
 * document visibilitychange + window pagehide/pageshow as a best-effort
 * equivalent.
 *
 * The callback receives a normalized event name. `pause`/`backgrounded` fire
 * when the app goes to background (or tab is hidden); `resume`/`foregrounded`
 * fire on return.
 *
 * Returns a subscription object with .remove(). Safe to call multiple times.
 */
export const subscribeToAppLifecycle = (
  onEvent: (event: AppLifecycleEvent) => void,
): AppLifecycleSubscription => {
  let removed = false
  const cleanupFns: Array<() => void> = []

  const remove = () => {
    if (removed) return
    removed = true
    cleanupFns.forEach((fn) => {
      try {
        fn()
      } catch {
        // Safe cleanup fallback
      }
    })
    cleanupFns.length = 0
  }

  // Web fallback / browser listeners
  const installWebListeners = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return

    const handleVisibility = () => {
      if (removed) return
      onEvent(document.hidden ? 'backgrounded' : 'foregrounded')
    }
    const handlePageHide = () => {
      if (removed) return
      onEvent('backgrounded')
    }
    const handlePageShow = () => {
      if (removed) return
      onEvent('foregrounded')
    }

    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('pageshow', handlePageShow)

    cleanupFns.push(() => {
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('pagehide', handlePageHide)
      window.removeEventListener('pageshow', handlePageShow)
    })
  }

  if (isNativePlatform()) {
    try {
      const nativeHandles: PluginListenerHandle[] = []

      const registerNative = async () => {
        const stateHandle = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
          if (removed) return
          onEvent(isActive ? 'foregrounded' : 'backgrounded')
        })
        if (removed) void stateHandle.remove()
        else nativeHandles.push(stateHandle)

        const pauseHandle = await CapacitorApp.addListener('pause', () => {
          if (removed) return
          onEvent('pause')
        })
        if (removed) void pauseHandle.remove()
        else nativeHandles.push(pauseHandle)

        const resumeHandle = await CapacitorApp.addListener('resume', () => {
          if (removed) return
          onEvent('resume')
        })
        if (removed) void resumeHandle.remove()
        else nativeHandles.push(resumeHandle)
      }

      void registerNative().catch(() => {
        // If native listeners fail to register, fall back to web listeners
        installWebListeners()
      })

      cleanupFns.push(() => {
        nativeHandles.forEach((handle) => void handle.remove())
        nativeHandles.length = 0
      })
    } catch {
      installWebListeners()
    }
  } else {
    installWebListeners()
  }

  return { remove }
}
